import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send, Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { CONTACT, STUDIOS } from "../data/site";
import { Reveal, MaskedLine } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { useI18n } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", phone: "", studio: "", arrival: "", departure: "", message: "" };

export default function Contact() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const preselected = STUDIOS.find((s) => s.slug === params.get("studio"));
  const [form, setForm] = useState({ ...initialForm, studio: preselected ? preselected.name : "" });
  const [loading, setLoading] = useState(false);

  const studioOptions = [
    { value: "Studio des Ô Glaciers", slug: "glaciers" },
    { value: "Studio des Ô Gentianes", slug: "gentianes" },
    { value: "Les deux studios", slug: "both", label: t("contact.both") },
  ];

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error(t("contact.errName"));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error(t("contact.errEmail"));
    if (form.message.trim().length < 10) return toast.error(t("contact.errMessage"));

    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        studio: form.studio || null,
        arrival: form.arrival || null,
        departure: form.departure || null,
        message: form.message.trim(),
      });
      toast.success(t("contact.success"));
      setForm(initialForm);
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-pine/25 focus:border-wood-dark outline-none py-3 text-base placeholder:text-ink/35 transition-colors duration-300 rounded-none";

  return (
    <div data-testid="contact-page" className="pt-40 pb-24 md:pb-32">
      <Seo titleKey="seo.contact.title" descKey="seo.contact.description" />
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <p className="text-xs uppercase tracking-[0.25em] text-pine-light font-semibold mb-6">{t("contact.overline")}</p>
        <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl tracking-tighter text-pine leading-[0.95]">
          <MaskedLine delay={0.1}>{t("contact.line1")}</MaskedLine>
          <MaskedLine delay={0.25}>
            <span className="italic text-wood-dark">{t("contact.line2a")}</span>
          </MaskedLine>
        </h1>

        <div className="grid md:grid-cols-12 gap-16 mt-16 md:mt-24">
          <Reveal className="md:col-span-7">
            <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-10">
              <div className="grid sm:grid-cols-2 gap-10">
                <div>
                  <label htmlFor="contact-name" className="text-xs uppercase tracking-[0.15em] text-ink/50">{t("contact.name")}</label>
                  <input id="contact-name" data-testid="contact-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder={t("contact.namePh")} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="text-xs uppercase tracking-[0.15em] text-ink/50">{t("contact.email")}</label>
                  <input id="contact-email" data-testid="contact-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder={t("contact.emailPh")} />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-10">
                <div>
                  <label htmlFor="contact-phone" className="text-xs uppercase tracking-[0.15em] text-ink/50">{t("contact.phone")}</label>
                  <input id="contact-phone" data-testid="contact-phone-input" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder={t("contact.phonePh")} />
                </div>
                <div>
                  <span className="text-xs uppercase tracking-[0.15em] text-ink/50 block mb-3">{t("contact.studioLabel")}</span>
                  <div className="flex flex-wrap gap-2">
                    {studioOptions.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        data-testid={`contact-studio-option-${o.slug}`}
                        onClick={() => setForm((f) => ({ ...f, studio: o.value }))}
                        className={`px-4 py-2 text-sm border transition-colors duration-300 ${
                          form.studio === o.value
                            ? "bg-pine text-cream border-pine"
                            : "border-pine/25 text-ink/70 hover:border-pine"
                        }`}
                      >
                        {o.label || o.value}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-10">
                <div>
                  <label htmlFor="contact-arrival" className="text-xs uppercase tracking-[0.15em] text-ink/50">{t("contact.arrival")}</label>
                  <input id="contact-arrival" data-testid="contact-arrival-input" type="date" className={inputCls} value={form.arrival} onChange={set("arrival")} />
                </div>
                <div>
                  <label htmlFor="contact-departure" className="text-xs uppercase tracking-[0.15em] text-ink/50">{t("contact.departure")}</label>
                  <input id="contact-departure" data-testid="contact-departure-input" type="date" className={inputCls} value={form.departure} onChange={set("departure")} />
                </div>
              </div>

              <div>
                <label htmlFor="contact-message" className="text-xs uppercase tracking-[0.15em] text-ink/50">{t("contact.message")}</label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  rows={5}
                  className={`${inputCls} resize-none`}
                  value={form.message}
                  onChange={set("message")}
                  placeholder={t("contact.messagePh")}
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="group inline-flex items-center gap-3 bg-pine text-cream px-8 py-4 text-sm font-semibold tracking-wide hover:bg-pine-light transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? <Loader2 aria-hidden className="w-4 h-4 animate-spin" /> : <Send aria-hidden className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />}
                {loading ? t("contact.sending") : t("contact.submit")}
              </button>
              <p className="text-xs text-ink/40 max-w-lg leading-relaxed">
                {t("contact.privacyNote").replace("{privacy}", CONTACT.privacy)}{" "}
                <a href="/politique-de-confidentialite" data-testid="contact-privacy-link" className="underline hover:text-pine transition-colors">
                  {t("footer.privacyPolicy")}
                </a>
              </p>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="md:col-span-5">
            <div className="bg-pine text-cream p-8 md:p-12 md:sticky md:top-28">
              <p className="text-xs uppercase tracking-[0.25em] text-wood font-semibold mb-8">{t("contact.directTitle")}</p>
              <a data-testid="contact-direct-phone" href={CONTACT.phoneHref} className="flex items-center gap-4 py-3 text-lg hover:text-wood transition-colors">
                <Phone aria-hidden className="w-5 h-5 text-wood" /> {CONTACT.phone}
              </a>
              <a data-testid="contact-direct-email" href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 py-3 text-lg hover:text-wood transition-colors break-all">
                <Mail aria-hidden className="w-5 h-5 text-wood shrink-0" /> {CONTACT.email}
              </a>
              <div className="flex items-start gap-4 py-3 text-lg text-cream/80">
                <MapPin aria-hidden className="w-5 h-5 text-wood shrink-0 mt-1" />
                <span>{CONTACT.resort}<br />{CONTACT.address}</span>
              </div>
              <p className="mt-8 pt-8 border-t border-cream/10 text-sm text-cream/60 leading-relaxed">
                {t("contact.directNote")}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
